/**
 * /datasets/:id/import — upload a CSV in four steps: file → columns → checks →
 * save. The file is read and checked in the browser; nothing is sent until the
 * last step. Rows without an expected answer are fine (a soft note only).
 *
 * The draft upload is this page's own state: it is one form, gone on reload.
 * Until the person moves a column, the mapping is guessed from the header. The
 * dataset belongs to no agent: which agent input each field feeds is chosen
 * when an experiment is set up.
 * `?returnTo=` (from the new-experiment flow) is where saving goes back to,
 * with `dataset=<id>` added so the flow picks it.
 */

import { useState } from "react";
import { useParams } from "react-router";
import { useUrlState } from "@/lib/use-url-state";
import { Button } from "@/components/ui/button";
import { StepNav } from "@/components/ui/step-nav";
import { Crumbs, PageHeader } from "@/components/ui/page";
import { QueryState } from "@/components/ui/states";
import { autoMap, checkRows, planImport, type Mapping } from "./csv-mapping";
import { useDataset } from "./hooks";
import { ImportCheck } from "./import-check";
import { ImportMap } from "./import-map";
import { ImportSave } from "./import-save";
import { ImportSource, type LoadedFile } from "./import-source";

const STEPS = ["Choose file", "Map columns", "Check rows", "Save"] as const;

/** An in-app path only: anything else would make this page an open redirect. */
function inApp(path: string | null): string | null {
  return path && path.startsWith("/") && !path.startsWith("//") ? path : null;
}

export function CsvImportPage() {
  const { id = "" } = useParams();
  const dataset = useDataset(id);
  const returnTo = inApp(useUrlState().get("returnTo"));
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<LoadedFile | null>(null);
  // null = not edited yet: follow the file.
  const [edited, setEdited] = useState<Mapping | null>(null);
  const mapping = edited ?? autoMap(file?.csv.header ?? []);

  const checks = file ? checkRows(file.csv.rows, mapping) : [];
  const plan = planImport(checks);
  const blocked = plan.items.length === 0;
  const reachable = !file ? 0 : blocked ? 2 : 3;
  const load = (f: LoadedFile) => (setFile(f), setEdited(null), setStep(1));
  const reset = () => (setFile(null), setEdited(null), setStep(0));

  return (
    <QueryState query={dataset} what="the dataset">
      {dataset.data && (
        <>
          <div className="flex flex-col gap-2">
            <Crumbs
              items={
                returnTo
                  ? [
                      { label: "New experiment", to: returnTo },
                      { label: dataset.data.name },
                      { label: "Upload" },
                    ]
                  : [
                      { label: "Datasets", to: "/datasets" },
                      { label: dataset.data.name, to: `/datasets/${id}` },
                      { label: "New version" },
                    ]
              }
            />
            <PageHeader
              title={`New version · v${(dataset.data.latest_version?.version ?? 0) + 1}`}
              subtitle="Upload a CSV of inputs with the outputs you expect. The file is read in your browser first; nothing is saved until the last step."
            />
          </div>
          <StepNav label="Upload steps" steps={STEPS} step={step} reachable={reachable} onPick={setStep} />
          {file && step > 0 && (
            <div className="box flex items-center gap-3.5 px-4 py-3">
              <span className="text-sm font-semibold">{file.name}</span>
              <span className="font-mono text-xs text-muted">
                {file.csv.rows.length} rows · {file.csv.header.length} columns
              </span>
              <span className="flex-1" />
              <Button size="sm" onClick={reset}>
                Replace file
              </Button>
            </div>
          )}
          {step === 0 && <ImportSource onLoad={load} />}
          {step === 1 && file && <ImportMap csv={file.csv} mapping={mapping} onChange={setEdited} />}
          {step === 2 && <ImportCheck checks={checks} plan={plan} />}
          {step === 3 && file && (
            <ImportSave
              dataset={dataset.data}
              plan={plan}
              fileName={file.name}
              returnTo={returnTo}
              onAgain={reset}
            />
          )}
          {step > 0 && step < 3 && (
            <div className="flex justify-end gap-2.5">
              <Button onClick={() => setStep(step - 1)}>Back</Button>
              <Button variant="primary" disabled={step === 2 && blocked} onClick={() => setStep(step + 1)}>
                {step === 1 ? "Check rows" : `Continue with ${plan.items.length} items`}
              </Button>
            </div>
          )}
        </>
      )}
    </QueryState>
  );
}
